import { zodResolver } from '@hookform/resolvers/zod'
import { sendGTMEvent } from '@next/third-parties/google'
import { HTTPError } from 'ky'
import { Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { loginUser } from '@/app/conta/login/action'
import {
  getStagingEmailErrorMessage,
  isEmailAllowedInStaging,
} from '@/helpers/staging-email-validation'
import { bff_client } from '@/lib/apiClient'
import type { EnumProductType } from '@/types/enum-product-type'
import { mapUserDataForEnhancedMatch } from '../@analitycs/map-user-data-for-enhanced-match'
import { buildGA4EventModel } from '../@analitycs/push-ecommerce-event'
import { trackEvent } from '../@analitycs/trackEvent'
import { Button } from '../ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form'
import { Input } from '../ui/input'
import { PhoneInput } from '../ui/phone-input'
import { enrolAfterAuthentication } from './enrol-after-authentication'

const TOAST_DURATION = 10_000
const HTTP_STATUS_FORBIDDEN = 403
const USER_ALREADY_REGISTERED_MESSAGE = 'User already registered.'

interface EnrolmentDataType {
  product_id: string
  product_external_id?: number
  workload_value?: number
  workload_id: string
  period_id: string
}

interface SignUpFormProps {
  email: string
  externalIdProduct: string | null
  referenceProduct: string
  enrolmentData: EnrolmentDataType | null
  productType: EnumProductType
}

export const signUpFormSchema = z
  .object({
    full_name: z.string().min(1, { message: 'O campo nome é obrigatorio' }),
    email: z
      .string()
      .min(1, { message: 'O campo e-mail é obrigatorio' })
      .email({ message: 'E-mail inválido' }),
    phone_number: z
      .string()
      .nonempty('Telefone é obrigatório')
      .transform(telefone => telefone.replace(/\D/g, '')),
    password: z
      .string()
      .nonempty({ message: 'O campo senha é obrigatorio' })
      .min(6, { message: 'Senha deve ter pelo menos 6 caracteres.' }),
    re_password: z
      .string()
      .nonempty({ message: 'O campo senha é obrigatorio' })
      .min(6, { message: 'Senha deve ter pelo menos 6 caracteres.' }),
  })
  .refine(data => data.password === data.re_password, {
    message: 'As senhas fornecidas não coincidem.',
    path: ['re_password'],
  })
  .refine(data => isEmailAllowedInStaging(data.email), {
    message: getStagingEmailErrorMessage(),
    path: ['email'],
  })

type SignUpFormType = z.infer<typeof signUpFormSchema>

function handleSignUpError(error: unknown) {
  if (error instanceof HTTPError) {
    return error.response.json().then(response => {
      const status = error.response.status

      if (
        status === HTTP_STATUS_FORBIDDEN &&
        response.messages?.error === USER_ALREADY_REGISTERED_MESSAGE
      ) {
        toast.error('Este e-mail já possui uma conta cadastrada', {
          description:
            'Caso tenha esquecido sua senha, utilize a opção de recuperação.',
          duration: TOAST_DURATION,
        })
        return
      }

      const errorMessage =
        response.messages?.error ||
        response.message ||
        'Ops, ocorreu um erro, tente novamente mais tarde!'
      toast.error(errorMessage, {
        duration: TOAST_DURATION,
      })
    })
  }

  toast.error('Ops, ocorreu um erro, tente novamente mais tarde!', {
    duration: TOAST_DURATION,
  })
}

export default function SignUpForm({
  email,
  enrolmentData,
  externalIdProduct,
  productType,
  referenceProduct,
}: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<SignUpFormType>({
    resolver: zodResolver(signUpFormSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      full_name: '',
      password: '',
      phone_number: '',
      re_password: '',
    },
  })

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting },
  } = form

  async function onSubmit({
    full_name,
    email,
    password,
    phone_number,
    re_password,
  }: SignUpFormType) {
    try {
      await bff_client.post('user/create', {
        json: {
          full_name,
          email,
          password,
          phone_number,
          re_password,
        },
      })

      const eventData = {
        event: 'vitrine',
        eventName: 'Lead',
        customDataObject: {
          environment: 'Vitrine',
        },
      }
      trackEvent(eventData)

      try {
        const userData = mapUserDataForEnhancedMatch({
          email,
          fullName: full_name,
          phone: phone_number,
        })
        const eventModel = await buildGA4EventModel({ userData })

        sendGTMEvent({
          event: 'generate_lead',
          eventModel,
          ...(eventModel.user_data && { user_data: eventModel.user_data }),
        })
      } catch {
        // Ignorado de propósito: analytics é best-effort e não deve
        // invalidar um cadastro já concluído.
      }

      const loginResult = await loginUser({ identifier: email, password })

      if (!loginResult?.data?.success) {
        toast.error('Não foi possível entrar na sua conta', {
          description: 'Verifique seus dados e tente novamente.',
          duration: 10_000,
        })
        return
      }

      if (enrolmentData) {
        await enrolAfterAuthentication({
          enrolmentData,
          externalIdProduct,
          productType,
          referenceProduct,
        })
      }
    } catch (error: unknown) {
      await handleSignUpError(error)
    }
  }

  useEffect(() => {
    if (email) {
      setValue('email', email)
    }
  }, [email, setValue])

  const { full_name, password, re_password, phone_number } =
    form.formState.errors

  const emailError = !!form.formState.errors.email?.message
  const fullNameError = !!full_name?.message
  const phoneNumberError = !!phone_number?.message
  const passwordError = !!password?.message
  const rePasswordError = !!re_password?.message

  return (
    <Form {...form}>
      <form method="post"
        className="mt-6 bg-white"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="space-y-4">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="relative space-y-0">
                <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                  Seu E-mail
                </FormLabel>
                <FormControl>
                  <Input
                    className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                    data-error={emailError}
                    disabled={isSubmitting}
                    placeholder="Digite seu e-mail"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="full_name"
            render={({ field }) => (
              <FormItem className="relative space-y-0">
                <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                  Seu nome completo
                </FormLabel>
                <FormControl>
                  <Input
                    className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                    data-error={fullNameError}
                    disabled={isSubmitting}
                    placeholder="Digite seu nome"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone_number"
            render={({ field }) => (
              <FormItem className="relative w-full space-y-0">
                <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 text-[#525252] text-xs">
                  Telefone celular
                </FormLabel>
                <FormControl>
                  {/* <Input placeholder="+55 (99) 9 9999-9999" className="text-sm border border-[#D4D4D4] rounded-sm bg-transparent focus:bg-[#FAFAFA] focus:border-[#B663FF] placeholder:text-[#737373]" {...field} /> */}
                  <PhoneInput
                    className="group data-[error=true]:border-red-500 [&_input]:border [&_input]:border-[#D4D4D4] [&_input]:bg-transparent [&_input]:text-sm [&_input]:focus:border-[#B663FF] [&_input]:data-[error=true]:border-red-500"
                    data-error={phoneNumberError}
                    defaultCountry="BR"
                    disabled={isSubmitting}
                    international={false}
                    maxLength={15}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem className="relative space-y-0">
                <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                  Crie uma senha
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                      data-error={passwordError}
                      disabled={isSubmitting}
                      placeholder="Digite sua senha"
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                    />
                  </FormControl>
                  <Button
                    className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sr-only">
                      {showPassword ? 'Hide password' : 'Show password'}
                    </span>
                  </Button>
                </div>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="re_password"
            render={({ field }) => (
              <FormItem className="relative space-y-0">
                <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                  Repita a senha
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                      data-error={rePasswordError}
                      disabled={isSubmitting}
                      placeholder="Digite sua senha novamente"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...field}
                    />
                  </FormControl>

                  <Button
                    className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sr-only">
                      {showConfirmPassword ? 'Hide password' : 'Show password'}
                    </span>
                  </Button>
                </div>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>

        <div className="mt-3.5">
          <Button
            className="h-10 rounded-1xl"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <LoaderCircle className="size-6 animate-spin" />
            ) : (
              'Avançar'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
